// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import {
    CheckoutContext,
    CheckoutActionTypes,
    recordNREvent,
    NEWRELIC_TABLES,
    useNavigate,
    CheckoutRoute,
    AnalyticsContextValue,
    AnalyticsContext,
} from "@swiggy-private/checkout-core";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// helpers
import { getEMIPaymentMethodData, getEMIPaymentGroup } from "@payment-client/helpers/CreditCardEMIHelper";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { EMI_TOAST_ERROR_TITLE } from "@payment-client/constants/EMIPayment";
import { SCREEN_NAME, CLICK_ACTIONS } from "@payment-client/analytics/constants";

// hooks
import { useOnPay } from "./UseOnPay";

// types
import { TOAST_TYPE } from "@swiggy-private/payment-types";

// helpers
import { isCategoryInstamart } from "@payment-client/helpers/Misc";

interface ReturnValue {
    onEMIGroupClick: VoidFunction;
}

const handleEMIGroupClick = (clickEvent: AnalyticsContextValue["clickEvent"]) => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_PAYMENT_GROUP_EMI,
        context: "nc-emi",
    });
};

export const useCreditCardEMIGroup = (): ReturnValue => {
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const navigateTo = useNavigate();
    const onPay = useOnPay();

    const emiPaymentGroup = useMemo(() => getEMIPaymentGroup(paymentResponse?.paymentGroup ?? []), [paymentResponse]);
    const showErrorToast = useCallback(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.EMI_PAYMENT_GROUP_ERROR,
        });
        setCheckoutState({
            type: CheckoutActionTypes.GENERIC_PAYMENT_TOAST,
            payload: {
                message: EMI_TOAST_ERROR_TITLE,
                type: TOAST_TYPE.ERROR,
            },
        });
    }, [setCheckoutState]);

    // makes place order call with selected payment method
    const onEMIGroupClick = useCallback(async () => {
        // Fetching only the first payment method from the group as confirmed from backend we will always get one for pilot
        // TODO: Once we enable multiple EMI options for all BL, we might need to handle this case

        if (isCategoryInstamart()) {
            handleEMIGroupClick(clickEvent);
            navigateTo(CheckoutRoute.EMI_SELECTION);
            return;
        }

        if (!isEmpty(emiPaymentGroup)) {
            const emiPaymentMethodData = getEMIPaymentMethodData(emiPaymentGroup);

            if (!isEmpty(emiPaymentMethodData)) {
                onPay(emiPaymentMethodData);
            } else {
                showErrorToast();
            }
        } else {
            showErrorToast();
        }
    }, [emiPaymentGroup, onPay, showErrorToast]);

    return { onEMIGroupClick };
};
